/**
 * The hub's Report tab: where the report is in the doctor and QA loop, the
 * way into the workspace, and the generated blank (ADR-0001, D-0053): what
 * the registry says about generating for this doctor and type, Generate as
 * a job, the versions built and the Word download.
 */

import { statusLabel, type Status } from '@beam/core/status';
import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router';
import type { MatterDetail } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Panel } from '../panels';
import { BlankPanel } from './BlankPanel';

export function reportSentence(status: Status, rounds: number): string {
  if (status < 6) return `The workspace opens when the case reaches 6 ${statusLabel(6)}. It is at ${status} ${statusLabel(status)}.`;
  if (status === 6) return 'The report is with the doctor for the first draft.';
  if (status === 7) return `The report is with QA${rounds > 0 ? `, round ${rounds}` : ''}.`;
  if (status === 8) return 'QA has sent suggestions; the report is with the doctor to decide them.';
  if (status >= 9 && status <= 12) return 'QA has completed the report. The workspace is read only.';
  return `The case is at ${status} ${statusLabel(status)}; the workspace reopens when it returns to the doctor.`;
}

export function ReportTab({ matterRef, detail, offline = false }: { matterRef: string; detail: MatterDetail | undefined; offline?: boolean }) {
  const phone = useIsPhone();
  if (!detail) return <Skeleton className="h-40 rounded-xl" />;
  const status = detail.matter.status;
  const reachable = status >= 6 && status <= 12;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
      <Panel title="Report" action={reachable ? (
        <Button asChild size={phone ? 'lg' : 'sm'} className={phone ? 'h-11' : undefined} data-primary="true">
          <Link to={`/matters/${encodeURIComponent(matterRef)}/workspace`}>Open the workspace <ArrowRightIcon aria-hidden="true" className="size-4" /></Link>
        </Button>
      ) : undefined}>
        <p className="text-sm">{reportSentence(status, detail.cycle.rounds)}</p>
        <p className="mt-2 text-sm text-beam-text-muted">
          {detail.matter.doctor.name} is the doctor. {detail.cycle.gates.openSuggestions > 0 ? `${detail.cycle.gates.openSuggestions} ${detail.cycle.gates.openSuggestions === 1 ? 'item is' : 'items are'} with the doctor to decide.` : 'Nothing is waiting on the doctor to decide.'}
        </p>
      </Panel>
      <BlankPanel matterRef={matterRef} offline={offline} />
    </div>
  );
}
